import { FormControl, InputLabel } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { useTrans } from "~/hooks/useTrans";
import { MySelect, MyMenuItem } from "~/mui/MySelect";
import { IUrlParams, IFetchOutput } from "~/interfaces";
import { IUser, TUserKeyRole } from "~/providers/interfaces";
import { apiUpdateProjectUser } from "~/providers/api";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	role: TUserKeyRole;
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IUpdateProjectUser {
	userData: IUser;
	params: IUrlParams;
	isInternal: boolean;
	projectLastUpdate: string;
	onClose?: () => void;
	onSave?: (userData: IUser, lastUpdate: string) => void;
}

export default function UpdateProjectUser({
	userData = null,
	params = null,
	isInternal = false,
	projectLastUpdate = "",
	onClose = () => {},
	onSave = () => {},

}: IUpdateProjectUser) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		role: userData.role,
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});

	function getWasChanged(newData: Partial<IState>) {
		const newState = {
			...state,
			...newData,
		};

		return newState.role !== userData.role;
	}

	function updateSelect(newData: Partial<IState>) {
		updateState({
			...newData,
			wasChanged: getWasChanged(newData),
		});
	}

	async function dialogOnSave() {
		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		let outputData: IFetchOutput<IUser> = null;

		if (isInternal) {
			outputData = await apiUpdateProjectUser("admin", params.projectId, userData.id, {
				role: state.role,
				lastUpdate: projectLastUpdate,
			}, lang);
		} else {
			outputData = await apiUpdateProjectUser("client", params.projectId, userData.id, {
				role: state.role,
				lastUpdate: projectLastUpdate,
			}, lang);
		}

		if (outputData.noErrors) {
			onSave(outputData.data, outputData.lastUpdate);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("usersPage.changeRoleTitle")} onClose={onClose} onSave={dialogOnSave} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg}
		cancelCaption={ trans("cancel") } saveCaption={ trans("usersPage.btnChangeRole")} disableSave={!state.wasChanged} confirmClose={state.wasChanged} className="updateProjectUserModal">
		<h2 className="updateProjectUserModal__title">
			{ userData.email }
		</h2>
		<div className="updateProjectUserModal__controls">
			<div className="updateProjectUserModal__container">
				<span className="updateProjectUserModal__containerTitle">
					{ trans("usersPage.changeRoleTitle") }:
				</span>
				<FormControl size="small" className="langSwitcher">
					<InputLabel>{ trans("userRole.role") }</InputLabel>
					<MySelect value={state.role} onChange={event => updateSelect({ role: event.target.value as TUserKeyRole })} label={ trans("userRole.role") } size="small" disabled={userData.role === "owner"}>
						<MyMenuItem value="admin">
							{ trans(`userRole.admin`) }
						</MyMenuItem>
						<MyMenuItem value="visitor">
							{ trans(`userRole.visitor`) }
						</MyMenuItem>
					</MySelect>
				</FormControl>
			</div>
		</div>
	</MyDialog>;
}
